import React, { useEffect, useState } from 'react';
import { listCandidateInterviews, listCandidateSchedules } from '../../api/candidatePortal';
import { formatInterviewType, getFitVerdictChip } from '../../utils/formatters';
import FeedbackBody from '../components/FeedbackBody';
import { canJoinScheduledInterview, probeAgent } from '../utils/agentProbe';
import VerdictBadge from '../components/VerdictBadge';

export default function CandidateInterviewsPage() {
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllPast, setShowAllPast] = useState(false);
  const [expandedSessionId, setExpandedSessionId] = useState(null);

  useEffect(() => {
    Promise.all([
      listCandidateSchedules().catch(() => ({ data: [] })),
      listCandidateInterviews().catch(() => ({ data: [] })),
    ])
      .then(([schedRes, intRes]) => {
        // Real data only. An empty list shows the empty state; never prototype data
        // or invented company names (a candidate must only see their own records).
        const schedules = Array.isArray(schedRes.data)
          ? schedRes.data.filter((s) => s.status === 'SCHEDULED' || s.status === 'ACTIVE')
          : [];
        const cleanedPast = Array.isArray(intRes.data) ? intRes.data : [];

        setUpcoming(schedules);
        setPast(cleanedPast);
      })
      .catch((err) => console.warn('[Interviews]', err.message))
      .finally(() => setLoading(false));

    probeAgent().then(setAgent);
  }, []);

  const openApp = (schedule) => {
    const gate = canJoinScheduledInterview(schedule);
    if (!gate.ok && schedule.status !== 'ACTIVE') return;
    const token = schedule.inviteToken || (schedule.inviteDeepLink || '').split('invite=')[1];
    if (token) window.location.href = `samastix://join?invite=${encodeURIComponent(token)}`;
  };

  const getInitials = (str) => {
    if (!str) return 'SA';
    const words = str.trim().split(/\s+/);
    if (words.length >= 2) return `${words[0][0]}${words[1][0]}`.toUpperCase();
    return str.slice(0, 2).toUpperCase();
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return '';
    try {
      const d = new Date(isoStr);
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  };

  // Bug #5 fix: Reasonable cap / pagination with "Show N earlier"
  const INITIAL_CAP = 3;
  const displayedPast = showAllPast ? past : past.slice(0, INITIAL_CAP);
  const hiddenCount = Math.max(0, past.length - INITIAL_CAP);

  if (loading) {
    return (
      <div className="py-24 text-center">
        <div className="w-8 h-8 border-2 border-[var(--teal-500)] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-[var(--ink-soft)]">Loading interviews…</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* ── Page Header ─────────────────────────────────────────────── */}
      <div>
        <h1
          className="text-2xl sm:text-[26px] font-semibold text-[var(--navy-950)] tracking-tight"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          My Interviews
        </h1>
        <p className="text-[13.5px] text-[var(--ink-soft)] mt-1">
          Upcoming sessions and past results.
        </p>
      </div>

      {/* ── UPCOMING ────────────────────────────────────────────────── */}
      <div>
        <p className="eyebrow mb-3">Upcoming</p>
        {!upcoming.length ? (
          <div className="card p-6 text-sm text-[var(--ink-soft)]">
            No upcoming interviews scheduled.
          </div>
        ) : (
          <div className="space-y-3">
            {upcoming.map((s) => {
              const gate = canJoinScheduledInterview(s);
              const canOpen = agent && (s.status === 'ACTIVE' || gate.ok);
              const modeLabel = formatInterviewType(s.mode || 'AUTONOMOUS');

              return (
                <div key={s.id} className="card p-5 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 rounded-[10px] bg-[var(--navy-900)] text-white flex items-center justify-center shrink-0 shadow-sm">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </div>

                    <div className="min-w-0">
                      <h3
                        className="text-sm font-semibold text-[var(--navy-950)] truncate"
                        style={{ fontFamily: 'var(--font-heading)' }}
                      >
                        {s.title || 'Interview'}{s.orgName ? ` · ${s.orgName}` : ''}
                      </h3>
                      <p className="text-xs text-[var(--ink-soft)] mt-0.5 truncate">
                        {s.scheduledAt
                          ? new Date(s.scheduledAt).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: 'numeric',
                            })
                          : 'Fri, Nov 21 · 4:30 PM IST'}{' '}
                        · {modeLabel}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    <span className="chip chip-teal">
                      In 2 days
                    </span>
                    <button
                      type="button"
                      onClick={() => openApp(s)}
                      className="btn btn-ghost !py-1.5 !px-4 !text-xs"
                    >
                      {s.status === 'ACTIVE' ? 'Join live' : 'Prepare'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── PAST ────────────────────────────────────────────────────── */}
      <div>
        <p className="eyebrow mb-3">Past</p>
        {!past.length ? (
          <div className="card p-6 text-sm text-[var(--ink-soft)]">
            No completed interviews yet.
          </div>
        ) : (
          <div>
            <div className="card overflow-hidden">
              {displayedPast.map((iv) => {
                const initials = getInitials(iv.orgName);
                // A verdict is shown only when the organization actually sent one. Never derive
                // it from the score: a missing score can be a technical failure (P0-09) or the
                // organization may have turned candidate feedback off.
                const verdict = iv.feedback?.overallVerdict || null;
                const { label: verdictLabel, chipClass } = verdict
                  ? getFitVerdictChip(verdict)
                  : { label: iv.outcomeLabel || 'Under review', chipClass: 'chip-navy' };
                const isExpanded = expandedSessionId === iv.sessionId;
                const modeLabel = formatInterviewType(iv.mode || 'SUPERVISED');

                return (
                  <div key={iv.sessionId}>
                    <div className="list-row flex items-center justify-between">
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-10 h-10 rounded-[10px] bg-[var(--navy-900)] text-white text-xs font-bold flex items-center justify-center shrink-0 shadow-sm">
                          {initials}
                        </div>

                        <div className="min-w-0">
                          <h3
                            className="text-sm font-semibold text-[var(--navy-950)] truncate"
                            style={{ fontFamily: 'var(--font-heading)' }}
                          >
                            {iv.title || 'Interview'}{iv.orgName ? ` · ${iv.orgName}` : ''}
                          </h3>
                          <p className="text-xs text-[var(--ink-soft)] mt-0.5 truncate">
                            {formatDate(iv.endedAt || iv.completedAt || iv.scheduledAt)} · {modeLabel}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 shrink-0 pl-2">
                        {iv.interviewScore != null && (
                          <span className="font-tabular text-xs font-semibold text-[var(--ink-soft)]">
                            {Math.round(iv.interviewScore)}
                            <span className="text-[11px] font-normal text-[var(--ink-faint)]">/100</span>
                          </span>
                        )}

                        <span className={`chip ${chipClass}`}>
                          {verdictLabel}
                        </span>

                        {iv.feedback && (
                          <button
                            type="button"
                            onClick={() => setExpandedSessionId(isExpanded ? null : iv.sessionId)}
                            className="text-xs font-semibold text-[var(--teal-600)] hover:text-[var(--teal-700)] ml-1"
                          >
                            {isExpanded ? 'Hide' : 'Feedback'}
                          </button>
                        )}
                      </div>
                    </div>

                    {isExpanded && iv.feedback && (
                      <div className="px-6 py-4 bg-[var(--teal-050)] border-b border-[var(--line-soft)] text-xs space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-[var(--navy-950)]">Performance Evaluation</span>
                          <VerdictBadge verdict={iv.feedback.overallVerdict} />
                        </div>
                        <div className="p-3 rounded-[10px] bg-white border border-[var(--line-soft)]">
                          <FeedbackBody feedback={iv.feedback} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination / Cap button */}
            {!showAllPast && hiddenCount > 0 && (
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => setShowAllPast(true)}
                  className="btn btn-ghost !py-2 !px-6 !text-xs mx-auto"
                >
                  Show {hiddenCount} earlier interviews
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
